import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { likesApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Heart, MapPin, Clock, Eye, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function Matches() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMatches = useCallback(async () => {
    try {
      const { data } = await likesApi.getMatches();
      // Filter out self-matches — exclude any match where the portfolio belongs to the current user
      const filtered = data.filter(
        (match) =>
          match.portfolio?.id !== user?.id &&
          match.portfolio?.user_id !== user?.id &&
          match.portfolio?.email !== user?.email
      );
      setMatches(filtered);
    } catch (error) {
      console.error('Failed to load matches:', error);
      toast.error('Failed to load matches');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f5f1ed]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8744f] mx-auto mb-4" />
          <p className="text-gray-500 animate-pulse">Loading matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f1ed]">
      {/* Top Bar — same style as FindPeople */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => navigate(`/p/${p.id}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Home</span>
        </button>
        <span className="font-bold text-[#e8744f]">
          ❤️ Your Matches
        </span>
        <span className="text-xs text-gray-500">{matches.length} match{matches.length !== 1 ? 'es' : ''}</span>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">

        {/* Hero blurb */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center shrink-0">
            <Heart size={24} className="text-red-400" fill="#fca5a5" />
          </div>
          <div>
            <h2 className="font-bold text-gray-900 text-base">You both liked each other!</h2>
            <p className="text-sm text-gray-400">Reach out and make a connection.</p>
          </div>
        </div>

        {/* Empty state */}
        {matches.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-100">
            <Search size={40} className="mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-bold text-gray-700 mb-2">No matches yet</h3>
            <p className="text-sm text-gray-400 mb-4">Keep browsing portfolios to find your match!</p>
            <Link
              to="/browse"
              className="inline-block text-sm font-medium text-[#e8744f] hover:underline"
            >
              Browse Portfolios →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="matches-list">
            {matches.map((match) => {
              const p = match.portfolio;
              return (
                <div
                  key={match.match_id}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                  onClick={() => navigate(`/p/${p.id}`)}
                >
                  {/* Card Header */}
                  <div className={`p-5 text-center ${
                    p.theme === 'dark'
                      ? 'bg-gradient-to-br from-gray-800 to-gray-900'
                      : p.theme === 'creative'
                      ? 'bg-gradient-to-br from-[#e8744f] to-[#d66440]'
                      : 'bg-gradient-to-br from-orange-50 to-white'
                  }`}>
                    <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-2 border-2 border-white/30">
                      {p.avatar_url ? (
                        <img src={p.avatar_url} alt={p.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-[#e8744f] flex items-center justify-center text-white text-xl font-bold">
                          {p.name?.charAt(0)}
                        </div>
                      )}
                    </div>

                    {/* Match badge */}
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-500 font-medium">
                      <Heart size={10} fill="#f87171" /> Matched
                    </span>

                    {p.actively_looking && (
                      <div className="mt-1">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-600">
                          • Actively looking
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 truncate">{p.name}</h3>
                    <p className="text-sm text-gray-500 truncate mb-2">{p.title}</p>

                    {/* Profession badge */}
                    {(p.profession || p.branch) && (
                      <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 mb-2">
                        🎓 {[p.branch, p.profession].filter(Boolean).join(' · ')}
                      </span>
                    )}

                    {/* Location & Experience */}
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                      {p.location && (
                        <span className="flex items-center gap-1">
                          <MapPin size={10} />{p.location}
                        </span>
                      )}
                      {p.years_experience && (
                        <span className="flex items-center gap-1">
                          <Clock size={10} />{p.years_experience}+ yrs
                        </span>
                      )}
                    </div>

                    {/* Skills */}
                    {p.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {p.skills.slice(0, 3).map((skill, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
                            {skill}
                          </span>
                        ))}
                        {p.skills.length > 3 && (
                          <span className="text-xs px-2 py-0.5 text-gray-400">
                            +{p.skills.length - 3}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-400">
                        {new Date(match.timestamp).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric'
                        })}
                      </span>
                      <button
                        className="text-xs text-[#e8744f] font-medium hover:underline"
                        onClick={e => { e.stopPropagation(); navigate(`/p/${p.id}`); }}
                      >
                        View Profile →
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}